import React from 'react';
import { Controller, type SubmitHandler, type UseFormSetError, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { planSchema, type PlanFormInput, type PlanFormOutput } from '../schemas/plan.schema';
import { Form, Input, Button, Label, Select } from '@/shared/components';
import { Checkbox } from '@/shared/components/form';

interface PlanFormProps {
    initialValues?: Partial<PlanFormInput>;
    onSubmit: SubmitHandler<PlanFormOutput>;
    isLoading?: boolean;
    submitLabel?: string;
    onSetErrorRef?: (setError: UseFormSetError<PlanFormInput>) => void;
}

export function PlanForm({ initialValues, onSubmit, isLoading = false, submitLabel = 'Save', onSetErrorRef }: PlanFormProps) {
    const { handleSubmit, control, setError, formState: { isSubmitting } } = useForm<PlanFormInput, unknown, PlanFormOutput>({
        resolver: zodResolver(planSchema),
        mode: 'onBlur',
        defaultValues: {
            name: initialValues?.name ?? '',
            description: initialValues?.description ?? '',
            status: initialValues?.status ?? 'active',
            is_looped: initialValues?.is_looped ?? false,
        },
    });

    React.useEffect(() => { onSetErrorRef?.(setError); }, [onSetErrorRef, setError]);

    return (
        <Form className="space-y-4 max-w-lg" onSubmit={handleSubmit(onSubmit)}>
            <div>
                <Label htmlFor="name">Name</Label>
                <Controller
                    name="name"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                        <Input
                            id="name"
                            placeholder="e.g. Standard Plan"
                            {...field}
                            error={!!error}
                            hint={error?.message}
                        />
                    )}
                />
            </div>

            <div>
                <Label htmlFor="description">Description <span className="text-mp-muted">(optional)</span></Label>
                <Controller
                    name="description"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                        <Input
                            id="description"
                            placeholder="Optional description"
                            value={field.value ?? ''}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            error={!!error}
                            hint={error?.message}
                        />
                    )}
                />
            </div>

            <div>
                <Label htmlFor="status">Status</Label>
                <Controller
                    name="status"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                        <Select
                            id="status"
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            error={!!error}
                            hint={error?.message}
                            options={[
                                { value: 'active', label: 'Active' },
                                { value: 'retired', label: 'Retired' },
                            ]}
                        />
                    )}
                />
            </div>

            <div>
                <Controller
                    name="is_looped"
                    control={control}
                    render={({ field }) => (
                        <Checkbox
                            id="is_looped"
                            checked={field.value ?? false}
                            onChange={field.onChange}
                            label="Looped schedule"
                            ariaLabel="Looped schedule"
                            disabled={isLoading || isSubmitting}
                        />
                    )}
                />
                <p className="text-sm text-mp-muted">
                    When looped, the schedule repeats after the last service, with every set point
                    offset by the last service&apos;s months and miles.
                </p>
            </div>

            <Button size="sm" disabled={isLoading || isSubmitting}>
                {isLoading || isSubmitting ? 'Saving...' : submitLabel}
            </Button>
        </Form>
    );
}
